import React from "react";
import clsx from "clsx";
import CloseIcon from "@ancon/wildcat-ui/shared/icons/close-x.svg";
import ErrorIcon from "@ancon/wildcat-ui/shared/icons/i-close-x.svg";
import SuccessIcon from "@ancon/wildcat-ui/shared/icons/check.svg";
import InfoIcon from "@ancon/wildcat-ui/shared/icons/info-fill.svg";

import styles from "./Toast.module.scss";
import BodyText from "../support/BodyText";
import { Button } from "reactstrap";

export type ToastVariant = "info" | "success" | "error";

function getToastVariantIcon(variant: ToastVariant) {
  switch (variant) {
    case "success":
      return SuccessIcon;

    case "error":
      return ErrorIcon;

    case "info":
    default:
      return InfoIcon;
  }
}

type ToastProps = {
  variant: ToastVariant;
  title: string;
  message?: string;
};

const Toast = ({ variant, title, message }: ToastProps) => {
  const ToastVariantIcon = getToastVariantIcon(variant);

  const variantColorClass = clsx({
    [styles.success]: variant === "success",
    [styles.error]: variant === "error",
    [styles.info]: variant === "info",
  });

  return (
    <div
      className={clsx(styles.toast, styles.animate, {
        [styles.slideEnter]: false,
        [styles.slideExit]: false,
      })}
      role="presentation"
    >
      <div className={clsx(styles.colorStripe, variantColorClass)} />
      <div className={styles.toastBody}>
        <div className={clsx(styles.iconCircle, variantColorClass)}>
          <ToastVariantIcon />
        </div>
        <div className={styles.toastContent}>
          <BodyText className={styles.title} color="body-0" fontSize="1.6rem">
            {title}
          </BodyText>
          {!!message && (
            <BodyText className={styles.message} color="body-1">
              {message}
            </BodyText>
          )}
        </div>
      </div>
      <Button className={styles.close} type="button" plain>
        <CloseIcon />
      </Button>
    </div>
  );
};

Toast.info = () => {
  throw new Error("Toast.info() was called before Toaster had mounted.");
};

Toast.success = () => {
  throw new Error("Toast.success() was called before Toaster had mounted.");
};

Toast.error = () => {
  throw new Error("Toast.error() was called before Toaster had mounted.");
};

export default Toast;
